import { useState, useEffect, useRef, createContext, useContext, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabaseClient } from './services/supabase';
import { abortControllers } from './utils/api';

const clearApiAuthAndRequests = () => {
  try {
    if (abortControllers && typeof abortControllers === 'object') {
      Object.values(abortControllers).forEach((ctl) => {
        try { ctl?.abort?.(); } catch {}
      });
      // remove keys so future requests don't reuse aborted controllers
      Object.keys(abortControllers).forEach(k => { try { delete abortControllers[k]; } catch {} });
    }
  } catch {}
};

const AuthContext = createContext(null);

// Páginas a las que se llega sin sesión. Con sesión, desde acá se va al dashboard;
// en cualquier otra página se deja al usuario donde está.
const PUBLIC_PATHS = ['/', '/login', '/register'];
const isPublicPath = () => PUBLIC_PATHS.includes(window.location.pathname);

const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutReason, setLogoutReason] = useState(null);
  const navigate = useNavigate();
  // useNavigate devuelve una función nueva en cada cambio de ruta: si fuera dependencia
  // del efecto de sesión, éste correría en cada navegación y devolvería al dashboard.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const clearLogoutReason = () => setLogoutReason(null);

  // Check session on mount and set up auth state listener
  useEffect(() => {
    // Get the initial session
    const getInitialSession = async () => {
      try {
        setLoading(true);
        const { data: { session }, error } = await supabaseClient.auth.getSession();
        
        if (error) throw error;
        
        setSession(session);
        setUser(session?.user ?? null);

        // Sin sesión en una página protegida, ProtectedLayout ya redirige a /login.
        if (session && isPublicPath()) {
          navigateRef.current('/dashboard');
        }
      } catch (error) {
        console.error('Error getting session:', error);
        navigateRef.current('/login');
      } finally {
        setLoading(false);
      }
    };

    // Set up the auth state change listener
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Supabase también emite SIGNED_IN al volver a la pestaña o refrescar el token:
        // sólo se redirige si el usuario está en una página pública.
        if (event === 'SIGNED_IN' && isPublicPath()) {
          navigateRef.current('/dashboard');
        } else if (event === 'SIGNED_OUT') {
          navigateRef.current('/login');
        }
      }
    );

    // Get the initial session
    getInitialSession();

    // Cleanup subscription on unmount
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // El perfil del usuario (public.profiles) lo crea un trigger en Supabase al registrarse,
  // venga de email o de Google; el front ya no avisa a ningún back.

  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      const { data, error } = await supabaseClient.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const signUp = useCallback(async (email, password, userData = {}) => {
    try {
      setLoading(true);
      const { data, error } = await supabaseClient.auth.signUp({
        email,
        password,
        options: {
          data: {
            ...userData,
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const signInWithOAuth = async (provider) => {
    try {
      setLoading(true);
      const { data, error } = await supabaseClient.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        },
      });
      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(async (reason = 'manual') => {
    try {
      setLoading(true);
      const { error } = await supabaseClient.auth.signOut();
      if (error) throw error;
      setSession(null);
      setUser(null);
      setLogoutReason(reason);
      clearApiAuthAndRequests();
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-logout due to inactivity (default 15 minutes; override via REACT_APP_INACTIVITY_MS)
  const INACTIVITY_MS = Number(process.env.REACT_APP_INACTIVITY_MS ?? 15 * 60 * 1000);

  useEffect(() => {
    if (!session) return; // only track when authenticated

    let timeoutId;
    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        logout('inactivity');
      }, INACTIVITY_MS);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((evt) => window.addEventListener(evt, resetTimer));
    resetTimer();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      events.forEach((evt) => window.removeEventListener(evt, resetTimer));
    };
  }, [session, INACTIVITY_MS, logout]);

  const value = useMemo(
    () => ({
      session,
      user,
      loading,
      login,
      signUp,
      signInWithOAuth,
      logout,
      logoutReason,
      clearLogoutReason,
    }),
    [session, user, loading, logoutReason, signUp, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, AuthContext };
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};